import { useQuery } from '@tanstack/react-query';
import { directApiInstance } from '../api/httpClient';

/**
 * SAM-1186 — `payment_link_status` per booking (`pending_payment` while a payment link is open). Only
 * `GET /bookings?business_id=` annotates it (SAM-633); `/crud/booking`, which the Activity rows come from because it
 * carries the guest and the service, does not. This reads the annotated list and keeps just the id → status map.
 * `GET /bookings` answers `{ data: { items, pagination } }`, at most 100 per page, newest reservation first.
 */
const PER_PAGE = 100;
const MAX_PAGES = 5;

type Page = { items?: Array<{ id: string; payment_link_status?: string | null }>; pagination?: { total?: number } };

export const useBookingLinkStatuses = (businessId: string | null, enabled = true) =>
  useQuery<Record<string, string>, Error>({
    queryKey: ['bookings', businessId, 'link-status'],
    enabled: !!businessId && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const out: Record<string, string> = {};
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const res = await directApiInstance<{ data?: Page } & Page>({
          url: '/bookings', method: 'GET', params: { business_id: businessId, per_page: PER_PAGE, page },
        });
        const pageData: Page = res.data?.data ?? res.data ?? {};
        const items = pageData.items ?? [];
        for (const b of items) if (b.payment_link_status) out[String(b.id)] = b.payment_link_status;
        if (items.length < PER_PAGE || page * PER_PAGE >= (pageData.pagination?.total ?? 0)) break;
      }
      return out;
    },
  });
